import { Stack, useMediaQuery, useTheme } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Door, DoorBatteryWarningLevel } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BatteryStatusCell from 'v2/components/BatteryStatusCell/BatteryStatusCell';
import { dataGridIdSlots } from 'v2/components/DataGrid/dataGridIdSlots';
import { renderHeaderWithId } from 'v2/components/DataGrid/renderHeaderWithId';
import DetailDataGridContainer from 'v2/components/DetailDataGridContainer/DetailDataGridContainer';
import LockIconNameCell from 'v2/components/LockIconNameCell/LockIconNameCell';
import SearchAndFilters, { FilterChip, FilterSection } from 'v2/components/SearchAndFilters/SearchAndFilters';
import { DATAGRID_DEFAULT_PAGE_SIZE, DATAGRID_PAGE_SIZE_OPTIONS, DATAGRID_ROW_HEIGHT } from 'v2/constants';
import { FilterOfficeModeState } from 'v2/filters/devices/deviceFilterTypes';
import { useSearch } from 'v2/hooks/useSearch/useSearch';
import { getDevicePath } from 'v2/paths';
import { getOfficeModeDisplay } from 'v2/utils/scheduleDisplay';

const searchKeys = ['name'];

interface Props {
  doors: Door[];
  onEditDevices: () => void;
}

interface DeviceFilterState {
  batteryUnknown: boolean;
  batteryEmpty: boolean;
  batteryCritical: boolean;
  batteryLow: boolean;
  batteryFull: boolean;
}

const batteryFilterDefault: DeviceFilterState = {
  batteryUnknown: false,
  batteryEmpty: false,
  batteryCritical: false,
  batteryLow: false,
  batteryFull: false
};

const DevicesTab: React.FC<Props> = ({ doors, onEditDevices }) => {
  const { translation } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [filterStr, setFilterStr] = useState('');
  const [batteryFilter, setBatteryFilter] = useState<DeviceFilterState>(batteryFilterDefault);
  const [officeModeFilter, setOfficeModeFilter] = useState<FilterOfficeModeState>(null);

  const anyBatteryEnabled =
    batteryFilter.batteryUnknown ||
    batteryFilter.batteryEmpty ||
    batteryFilter.batteryCritical ||
    batteryFilter.batteryLow ||
    batteryFilter.batteryFull;

  const isAnyFilterEnabled = anyBatteryEnabled || officeModeFilter !== null;

  let filteredDoors = useSearch({ data: doors, search: filterStr, keys: searchKeys });

  if (anyBatteryEnabled) {
    filteredDoors = filteredDoors.filter(
      (door) =>
        (batteryFilter.batteryUnknown && door.batteryWarningLevel === DoorBatteryWarningLevel.BatteryUnknown) ||
        (batteryFilter.batteryFull && door.batteryWarningLevel === DoorBatteryWarningLevel.BatteryDefault) ||
        (batteryFilter.batteryLow && door.batteryWarningLevel === DoorBatteryWarningLevel.BatteryLow) ||
        (batteryFilter.batteryCritical && door.batteryWarningLevel === DoorBatteryWarningLevel.BatteryCritical) ||
        (batteryFilter.batteryEmpty &&
          (door.batteryWarningLevel === DoorBatteryWarningLevel.BatteryEmpty ||
            door.batteryWarningLevel === DoorBatteryWarningLevel.Powered))
    );
  }

  if (officeModeFilter === FilterOfficeModeState.HasOfficeMode) {
    filteredDoors = filteredDoors.filter((door) => {
      const schedule = door.officeMode;
      return schedule?.weekDays && schedule.weekDays.length > 0;
    });
  } else if (officeModeFilter === FilterOfficeModeState.NoOfficeMode) {
    filteredDoors = filteredDoors.filter((door) => {
      const schedule = door.officeMode;
      return !schedule?.weekDays || schedule.weekDays.length === 0;
    });
  }

  const toggleBattery = (key: keyof DeviceFilterState) => {
    setBatteryFilter((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const sections: FilterSection[] = [
    {
      id: 'battery_status',
      header: translation.battery_status,
      type: 'checkbox',
      isClearDisabled: !anyBatteryEnabled,
      onClear: () => setBatteryFilter(batteryFilterDefault),
      items: [
        {
          key: 'batteryUnknown',
          label: translation.unknown,
          checked: batteryFilter.batteryUnknown,
          onChange: () => toggleBattery('batteryUnknown')
        },
        {
          key: 'batteryEmpty',
          label: translation.empty,
          checked: batteryFilter.batteryEmpty,
          onChange: () => toggleBattery('batteryEmpty')
        },
        {
          key: 'batteryCritical',
          label: translation.critical,
          checked: batteryFilter.batteryCritical,
          onChange: () => toggleBattery('batteryCritical')
        },
        {
          key: 'batteryLow',
          label: translation.low,
          checked: batteryFilter.batteryLow,
          onChange: () => toggleBattery('batteryLow')
        },
        {
          key: 'batteryFull',
          label: translation.full,
          checked: batteryFilter.batteryFull,
          onChange: () => toggleBattery('batteryFull')
        }
      ]
    },
    {
      id: 'office_mode',
      header: translation.office_mode,
      type: 'radio',
      isClearDisabled: officeModeFilter === null,
      onClear: () => setOfficeModeFilter(null),
      items: [
        {
          key: FilterOfficeModeState.HasOfficeMode,
          label: translation.has_office_mode,
          checked: officeModeFilter === FilterOfficeModeState.HasOfficeMode,
          onChange: () => setOfficeModeFilter(FilterOfficeModeState.HasOfficeMode)
        },
        {
          key: FilterOfficeModeState.NoOfficeMode,
          label: translation.no_office_mode,
          checked: officeModeFilter === FilterOfficeModeState.NoOfficeMode,
          onChange: () => setOfficeModeFilter(FilterOfficeModeState.NoOfficeMode)
        }
      ]
    }
  ];

  const chips: FilterChip[] = [
    ...(batteryFilter.batteryUnknown
      ? [{ label: translation.battery_state_unknown, onDelete: () => toggleBattery('batteryUnknown') }]
      : []),
    ...(batteryFilter.batteryEmpty
      ? [{ label: translation.battery_empty, onDelete: () => toggleBattery('batteryEmpty') }]
      : []),
    ...(batteryFilter.batteryCritical
      ? [{ label: translation.battery_critical, onDelete: () => toggleBattery('batteryCritical') }]
      : []),
    ...(batteryFilter.batteryLow
      ? [{ label: translation.battery_low, onDelete: () => toggleBattery('batteryLow') }]
      : []),
    ...(batteryFilter.batteryFull
      ? [{ label: translation.battery_full, onDelete: () => toggleBattery('batteryFull') }]
      : []),
    ...(officeModeFilter === FilterOfficeModeState.HasOfficeMode
      ? [{ label: translation.has_office_mode, onDelete: () => setOfficeModeFilter(null) }]
      : []),
    ...(officeModeFilter === FilterOfficeModeState.NoOfficeMode
      ? [{ label: translation.no_office_mode, onDelete: () => setOfficeModeFilter(null) }]
      : [])
  ];

  const headerWithId = renderHeaderWithId();

  const columns: GridColDef<Door>[] = [
    {
      field: 'name',
      headerName: translation.name,
      flex: 1,
      sortable: true,
      renderHeader: headerWithId,
      renderCell: (params) => <LockIconNameCell door={params.row} />
    },
    {
      field: 'officeMode',
      headerName: translation.office_mode,
      flex: 1,
      sortable: true,
      renderHeader: headerWithId,
      valueGetter: (_, row) => getOfficeModeDisplay(row.officeMode)
    },
    {
      field: 'hardwareIdentifier',
      headerName: translation.serial_number,
      flex: 1,
      sortable: true,
      renderHeader: headerWithId,
      valueGetter: (_, row) => row.hardwareIdentifier
    },
    // TODO: bring back when available in the API
    // {
    //   field: 'firmware',
    //   headerName: translation.firmware,
    //   flex: 1,
    //   sortable: false,
    //   renderCell: () => 'Coming soon'
    // },
    {
      field: 'batteryWarningLevel',
      headerName: translation.status,
      flex: 1,
      sortable: true,
      renderHeader: headerWithId,
      renderCell: (params) => <BatteryStatusCell level={params.row.batteryWarningLevel} />
    }
  ];

  return (
    <Stack spacing={2}>
      <SearchAndFilters
        filterStr={filterStr}
        onFilterStrChange={setFilterStr}
        sections={sections}
        chips={chips}
        countCaption={`${filteredDoors.length} ${translation.of} ${doors.length} ${translation.devices}`}
        actionButton={{ id: 'edit-devices-button', label: translation.edit_devices, onClick: onEditDevices }}
      />
      <DetailDataGridContainer>
        <DataGrid
          className="background-level2"
          rows={filteredDoors}
          columns={columns}
          columnVisibilityModel={{
            officeMode: isDesktop,
            hardwareIdentifier: isDesktop,
            firmware: isDesktop
          }}
          autoHeight
          hideFooter={filteredDoors.length <= DATAGRID_DEFAULT_PAGE_SIZE}
          pageSizeOptions={DATAGRID_PAGE_SIZE_OPTIONS}
          initialState={{ pagination: { paginationModel: { pageSize: DATAGRID_DEFAULT_PAGE_SIZE } } }}
          rowHeight={DATAGRID_ROW_HEIGHT}
          onRowClick={(params) => navigate(getDevicePath(params.row.id))}
          disableRowSelectionOnClick
          disableColumnMenu
          slots={dataGridIdSlots}
        />
      </DetailDataGridContainer>
    </Stack>
  );
};

export default DevicesTab;
